/** Login and session checks use 401 for a wrong password or a missing token. */
export function isAuthCredentialPath(path: string): boolean {
  const route = path.split('?')[0] ?? ''
  return /^\/auth\/(login|register|me|logout|forgot-password|reset-password|password|account)(\/|$)/.test(route)
}

export type SessionLoginOffer = 'none' | 'blocking'

/**
 * Whether a failed API call should ask the user to sign in again.
 * Server errors, timeouts, and network failures are not an expired token.
 * Wrong-password responses stay on the form that submitted them.
 */
export function sessionLoginOffer(input: {
  status: number
  code?: string | null
  path: string
}): SessionLoginOffer {
  if (input.status !== 401) return 'none'
  if (isAuthCredentialPath(input.path)) return 'none'
  if (input.code === 'invalidCredentials' || input.code === 'accountNotFound' || input.code === 'invalidPhone') return 'none'
  // Guests and expired sessions get the same popup, then the request retries.
  return 'blocking'
}

export type UnauthenticatedAccess = 'allow' | 'popup'

/**
 * A protected app route with no session stays on that route and shows the
 * login popup. Guest-allowed pages and the public login page are unchanged.
 */
export function decideUnauthenticatedAccess(input: {
  toPath: string
  authenticated: boolean
  guestAllowed: boolean
}): UnauthenticatedAccess {
  const isApp = input.toPath === '/app' || input.toPath.startsWith('/app/')
  if (!isApp || input.authenticated || input.guestAllowed) return 'allow'
  return 'popup'
}

type ReauthWait = {
  promise: Promise<boolean>
  resolve: (ok: boolean) => void
}

let pending: ReauthWait | null = null
let lastResume = false

/** One in-flight sign-in for every 401 that arrives together. */
export function beginSessionReauth(): { promise: Promise<boolean>, alreadyWaiting: boolean } {
  if (pending) return { promise: pending.promise, alreadyWaiting: true }
  let resolve!: (ok: boolean) => void
  const promise = new Promise<boolean>((res) => {
    resolve = res
  })
  pending = { promise, resolve }
  return { promise, alreadyWaiting: false }
}

/** Resolves every request waiting on the sign-in popup. */
export function finishSessionReauth(ok: boolean): boolean {
  const current = pending
  if (ok) lastResume = current != null
  pending = null
  current?.resolve(ok)
  return current != null && ok
}

/** True once, after a login that resumed requests paused for a dead session. */
export function takeSessionResume(): boolean {
  const value = lastResume
  lastResume = false
  return value
}

export function resetSessionReauthForTests(): void {
  if (pending) pending.resolve(false)
  pending = null
  lastResume = false
}
